import { useState, useMemo } from "react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { normalizeSymbol } from "../../../shared/utils";
import { pnlColor, winRateColor } from "../chart/chartColors";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

type SortCol = "totalPips" | "avgPips" | "winRate" | "avgPnl";

export default function PipBySymbolWidget() {
  const { analyticsData, isDark, t } = useDashboardData();

  const tp = t.analytics.pips;

  const [sortCol, setSortCol] = useState<SortCol>("totalPips");
  const [sortAsc, setSortAsc] = useState(false);

  const pipBySymbol = analyticsData?.pipBySymbol ?? [];

  const sortedSymbols = useMemo(() => {
    return [...pipBySymbol]
      .sort((a, b) => {
        const va = Math.abs(a[sortCol] as number);
        const vb = Math.abs(b[sortCol] as number);
        return sortAsc ? va - vb : vb - va;
      })
      .slice(0, 10);
  }, [pipBySymbol, sortCol, sortAsc]);

  function toggleSort(col: SortCol) {
    if (sortCol === col) setSortAsc((v) => !v);
    else { setSortCol(col); setSortAsc(false); }
  }

  const mutedText = isDark ? "widget-label" : "widget-label";
  const thClass = `p-2 text-right text-xs cursor-pointer hover:text-foreground ${mutedText} select-none`;

  if (!analyticsData || pipBySymbol.length === 0) {
    return (
      <Card className="h-full">
        <CardSectionHeader label={tp.sectionTitle} isDark={isDark} />
        <CardContent className="h-full p-4">
          <EmptyState label={tp.noData} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardSectionHeader
        label={tp.sectionTitle}
        title={tp.bySymbol}
        isDark={isDark}
        inline
      />
      <CardContent className="px-5 pb-5 pt-1">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 text-xs text-muted-foreground">{t.analytics.symbol}</th>
                <th className="text-right p-2 text-xs text-muted-foreground">{t.journal.trades}</th>
                <th className={thClass} onClick={() => toggleSort("avgPips")}>
                  {tp.avgPipsCol}{" "}
                  {sortCol === "avgPips" ? (sortAsc ? "↑" : "↓") : ""}
                </th>
                <th className={thClass} onClick={() => toggleSort("totalPips")}>
                  {tp.totalPipsCol}{" "}
                  {sortCol === "totalPips" ? (sortAsc ? "↑" : "↓") : ""}
                </th>
                <th className={thClass} onClick={() => toggleSort("winRate")}>
                  {t.analytics.winRate}{" "}
                  {sortCol === "winRate" ? (sortAsc ? "↑" : "↓") : ""}
                </th>
                <th className={thClass} onClick={() => toggleSort("avgPnl")}>
                  {t.analytics.averagePnl}{" "}
                  {sortCol === "avgPnl" ? (sortAsc ? "↑" : "↓") : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedSymbols.map((s) => (
                <tr key={s.symbol} className="border-b hover:bg-muted/50">
                  <td className="p-2 font-medium">{normalizeSymbol(s.symbol)}</td>
                  <td className="p-2 text-right">{s.trades}</td>
                  <td
                    className="p-2 text-right font-mono"
                    style={{ color: pnlColor(s.avgPips) }}
                  >
                    {s.avgPips >= 0 ? "+" : ""}{s.avgPips.toFixed(1)}
                  </td>
                  <td
                    className="p-2 text-right font-mono"
                    style={{ color: pnlColor(s.totalPips) }}
                  >
                    {s.totalPips >= 0 ? "+" : ""}{s.totalPips.toFixed(1)}
                  </td>
                  <td
                    className="p-2 text-right font-mono"
                    style={{ color: winRateColor(s.winRate) }}
                  >
                    {s.winRate.toFixed(1)}%
                  </td>
                  <td
                    className="p-2 text-right font-mono"
                    style={{ color: pnlColor(s.avgPnl) }}
                  >
                    {s.avgPnl >= 0 ? "+" : ""}{s.avgPnl.toFixed(2)} $
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
